/*!
=========================================================
* Muse Ant Design Dashboard - v1.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/muse-ant-design-dashboard
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/muse-ant-design-dashboard/blob/main/LICENSE.md)
* Coded by Creative Tim
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Radio,
  Table,
  Upload,
  message,
  Progress,
  Button,
  Avatar,
  Typography,
  Dropdown,
  Input,
  Modal,
  Spin,
  Tag,
  Divider,
  Tabs,
  Badge,
} from "antd";

import {
  ToTopOutlined,
  SearchOutlined,
  ZoomInOutlined,
  ShopOutlined,
  WalletOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

// Images par défaut
import face from "../assets/images/face-1.jpg";
import pencil from "../assets/images/pencil.svg";

const { Title } = Typography;
const { TabPane } = Tabs;

const formProps = {
  name: "file",
  action: "https://www.mocky.io/v2/5cc8019d300000980a055e76",
  headers: {
    authorization: "authorization-text",
  },
  onChange(info) {
    if (info.file.status !== "uploading") {
      console.log(info.file, info.fileList);
    }
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  },
};

// Colonnes du tableau - même structure que tableWrapper.tsx
const columns = [
  {
    title: "NOM COMPLET",
    dataIndex: "fullname",
    key: "fullname",
    width: "15%",
  },
  {
    title: "TÉLÉPHONE",
    dataIndex: "phone",
    key: "phone",
    width: "15%",
  },
  {
    title: "STATUS",
    dataIndex: "status",
    key: "status",
    width: "15%",
  },
  {
    title: "ORGANISATIONS",
    dataIndex: "organisationsCount",
    key: "organisationsCount",
    width: "15%",
  },
  {
    title: "DATE DE CRÉATION",
    dataIndex: "createdAt",
    key: "createdAt",
    width: "20%",
  },
  {
    title: "ACTIONS",
    key: "actions",
    dataIndex: "actions",
  },
];

export default function Commercial() {
  const [commercials, setCommercials] = useState([]);
  const [filteredCommercials, setFilteredCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [commercialsDetails, setCommercialsDetails] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 15;

  const navigate = useNavigate();

  // Nouveaux états pour les actions
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Nouveaux états pour les organisations
  const [organisationsModalVisible, setOrganisationsModalVisible] =
    useState(false);
  const [selectedCommercialOrganisations, setSelectedCommercialOrganisations] =
    useState([]);
  const [selectedCommercialName, setSelectedCommercialName] = useState("");

  // Récupération des utilisateurs depuis l'API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);

        // Debug : vérifier l'URL
        const apiUrl = `${process.env.REACT_APP_COM_URL}/commercials`;
        console.log("URL de l'API:", apiUrl);
        console.log("REACT_APP_COM_URL:", process.env.REACT_APP_COM_URL);

        const res = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Ajoutez d'autres headers si nécessaire (Authorization, etc.)
          },
        });

        console.log("Status de la réponse:", res.status);
        console.log("Headers de la réponse:", res.headers);

        if (!res.ok) {
          throw new Error(`Erreur HTTP: ${res.status} - ${res.statusText}`);
        }

        const data = await res.json();
        console.log("Données reçues de l'API:", data);
        console.log("Nombre d'éléments:", data.length);

        if (!Array.isArray(data)) {
          throw new Error("Les données reçues ne sont pas un tableau");
        }

        // Transformation des données pour correspondre au format du tableau
        const transformedData = data.map((user, index) => {
          console.log(`Transformation utilisateur ${index}:`, user);

          return {
            key: user.uuid || index.toString(),
            userUuid: user.uuid,
            fullname: user.fullname || "N/A",
            phone: user.phone ? `+${user.phone}` : "N/A",
            status: (
              <Tag
                color={user.status === "active" ? "green" : "red"}
                bordered={false}
              >
                {user.status}
              </Tag>
            ),
            organisationsCount: (
              <Badge count={user.organisations?.length || 0} showZero>
                <ShopOutlined style={{ fontSize: "16px" }} />
              </Badge>
            ),
            createdAt: user.createdAt
              ? new Date(user.createdAt).toUTCString()
              : "N/A",
            actions: (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "organisations",
                      label: (
                        <span
                          onClick={() => {
                            //showOrganisationsModal(user)
                            navigate(`/commercials/${user.uuid}`);
                          }}
                        >
                          <ShopOutlined /> Voir les organisations (
                          {user.organisations?.length || 0})
                        </span>
                      ),
                    },
                    {
                      key: "details",
                      label: (
                        <span
                          onClick={() => {
                            //navigate(`/commercials/${user.uuid}`);
                            fetchCommercialsDetails(user.uuid);
                          }}
                        >
                          <ZoomInOutlined /> Voir les détails
                        </span>
                      ),
                    },
                  ],
                }}
                placement="bottomRight"
              >
                <Button loading={actionLoading}>Actions</Button>
              </Dropdown>
            ),

            // Données brutes pour le filtrage
            rawData: {
              fullname: user.fullname,
              phone: user.phone ? `+${user.phone}` : "",
              status: user.status,
              createdAt: user.createdAt
                ? new Date(user.createdAt).toUTCString()
                : "",
              organisations: user.organisations || [],
            },
          };
        });

        console.log("Données transformées:", transformedData);

        setCommercials(transformedData);
        setFilteredCommercials(transformedData);
      } catch (error) {
        console.error("Erreur complète:", error);
        console.error("Message d'erreur:", error.message);
        console.error("Stack trace:", error.stack);

        message.error(
          `Erreur lors du chargement des données: ${error.message}`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fonction pour afficher le modal des organisations
  const showOrganisationsModal = (commercial) => {
    setSelectedCommercialOrganisations(commercial.organisations || []);
    setSelectedCommercialName(commercial.fullname || "Commercial");
    setOrganisationsModalVisible(true);
  };

  // Fonction pour récupérer les détails d'un utilisateur
  const fetchCommercialsDetails = async (userUuid) => {
    try {
      setDetailsLoading(true);

      // Lancer les deux requêtes en parallèle
      const [userRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_COM_URL}/commercials/${userUuid}`),
      ]);

      if (!userRes.ok) {
        throw new Error("Utilisateur introuvable");
      }

      const userData = await userRes.json();

      // Ajouter les organisations aux détails utilisateur
      setCommercialsDetails({
        ...userData,
      });

      setModalVisible(true);
    } catch (error) {
      console.error("Erreur :", error.message);
      message.error("Erreur lors du chargement des détails");
      setCommercialsDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Filtrage des utilisateurs avec pagination manuelle
  useEffect(() => {
    let filtered = commercials;

    // Filtrage par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter((user) => {
        const searchableText = `${user.rawData.fullname} ${user.rawData.phone}`;
        return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtrage par statut (pour l'instant tous sont "online")
    if (statusFilter !== "all") {
      // Vous pouvez ajouter une logique de filtrage par statut ici
      // basée sur les données réelles de votre API
    }

    setFilteredCommercials(filtered);
  }, [commercials, searchTerm, statusFilter]);

  // Pagination manuelle
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredCommercials.slice(
    indexOfFirstUser,
    indexOfLastUser
  );
  const totalPages = Math.ceil(filteredCommercials.length / usersPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const onChange = (e) => {
    setStatusFilter(e.target.value);
    console.log(`radio checked:${e.target.value}`);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Réinitialiser à la première page lors de la recherche
  };

  // Fonction pour formater la balance du wallet
  const formatBalance = (balance) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(balance || 0);
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Tous les clients"
              extra={
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                    }}
                  >
                    <Input
                      placeholder="Rechercher des utilisateurs"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: 250 }}
                      prefix={<SearchOutlined />}
                    />
                    <Radio.Group onChange={onChange} defaultValue="all">
                      <Radio.Button value="all">Tous</Radio.Button>
                      <Radio.Button value="online">ONLINE</Radio.Button>
                    </Radio.Group>
                  </div>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={currentUsers}
                  loading={loading}
                  pagination={false} // Désactivation de la pagination automatique
                  className="ant-border-space font-semibold"
                />
              </div>

              {/* Pagination manuelle comme dans tableWrapper.tsx */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "16px",
                }}
              >
                <Button onClick={handlePrevPage} disabled={currentPage === 1}>
                  Précédent
                </Button>
                <span>
                  Page {currentPage} sur {totalPages} (
                  {filteredCommercials.length} utilisateurs)
                </span>
                <Button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Modal pour afficher les détails de l'utilisateur */}
        <Modal
          title="Détails de l'utilisateur"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              Fermer
            </Button>,
          ]}
          width={600}
        >
          {detailsLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin size="large" />
            </div>
          ) : commercialsDetails ? (
            <div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <p>
                    <strong>Nom complet :</strong>{" "}
                    {commercialsDetails.fullname || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Téléphone :</strong> +
                    {commercialsDetails.phone || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Date de création :</strong>{" "}
                    {commercialsDetails.createdAt
                      ? new Date(
                          commercialsDetails.createdAt
                        ).toLocaleDateString()
                      : "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>ID :</strong> {commercialsDetails.uuid || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Statut :</strong>{" "}
                    <Tag
                      color={
                        commercialsDetails.status === "active" ? "green" : "red"
                      }
                    >
                      {commercialsDetails.status}
                    </Tag>
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Organisations liées :</strong>{" "}
                    <Badge
                      count={commercialsDetails.organisations?.length || 0}
                      showZero
                    />
                  </p>
                </Col>
              </Row>
            </div>
          ) : (
            <p>Erreur lors du chargement des détails</p>
          )}
        </Modal>

        {/* Modal pour afficher les organisations */}
        <Modal
          title={`Organisations de ${selectedCommercialName}`}
          open={organisationsModalVisible}
          onCancel={() => setOrganisationsModalVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setOrganisationsModalVisible(false)}
            >
              Fermer
            </Button>,
          ]}
          width={900}
        >
          {selectedCommercialOrganisations.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <ShopOutlined style={{ fontSize: "48px", color: "#ccc" }} />
              <p style={{ marginTop: "16px", color: "#999" }}>
                Aucune organisation liée à ce commercial
              </p>
            </div>
          ) : (
            <div>
              {selectedCommercialOrganisations.map((org, index) => (
                <Card
                  key={org.id || index}
                  style={{ marginBottom: "16px" }}
                  size="small"
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <ShopOutlined />
                      {org.name}
                      <Tag color={org.status === "active" ? "green" : "red"}>
                        {org.status}
                      </Tag>
                    </div>
                  }
                  extra={
                    <Tag
                      color={
                        org.organisationType === "main" ? "blue" : "orange"
                      }
                    >
                      {org.organisationType}
                    </Tag>
                  }
                >
                  <Tabs defaultActiveKey="1" size="small">
                    <TabPane tab="Informations générales" key="1">
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <p>
                            <strong>ID Organisation :</strong>{" "}
                            {org.organisationId}
                          </p>
                          <p>
                            <strong>Téléphone :</strong> +{org.phone}
                          </p>
                          <p>
                            <strong>Type de compte :</strong> {org.accountType}
                          </p>
                        </Col>
                        <Col span={12}>
                          <p>
                            <strong>Date de création :</strong>{" "}
                            {new Date(org.createdAt).toLocaleDateString()}
                          </p>
                          <p>
                            <strong>Dernière MAJ :</strong>{" "}
                            {new Date(org.updatedAt).toLocaleDateString()}
                          </p>
                          {org.parentId && (
                            <p>
                              <strong>Organisation parent :</strong>{" "}
                              {org.parentId}
                            </p>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                    <TabPane
                      tab={
                        <span>
                          <WalletOutlined /> Portefeuille
                        </span>
                      }
                      key="2"
                    >
                      {org.wallet ? (
                        <Row gutter={[16, 16]}>
                          <Col span={8}>
                            <Card
                              size="small"
                              style={{ background: "#f6ffed" }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#666",
                                }}
                              >
                                Solde
                              </p>
                              <h3 style={{ margin: 0, color: "#52c41a" }}>
                                {formatBalance(org.wallet.balance)}
                              </h3>
                            </Card>
                          </Col>
                          <Col span={8}>
                            <Card
                              size="small"
                              style={{ background: "#e6f7ff" }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#666",
                                }}
                              >
                                Collecte
                              </p>
                              <h3 style={{ margin: 0, color: "#1890ff" }}>
                                {formatBalance(org.wallet.collect)}
                              </h3>
                            </Card>
                          </Col>
                          <Col span={8}>
                            <Card
                              size="small"
                              style={{ background: "#fff7e6" }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#666",
                                }}
                              >
                                Transfert
                              </p>
                              <h3 style={{ margin: 0, color: "#fa8c16" }}>
                                {formatBalance(org.wallet.transfer)}
                              </h3>
                            </Card>
                          </Col>
                          <Col span={24}>
                            <p>
                              <strong>ID Portefeuille :</strong>{" "}
                              {org.wallet.walletId}
                            </p>
                            <p>
                              <strong>Statut :</strong>
                              <Tag
                                color={
                                  org.wallet.statut === "active"
                                    ? "green"
                                    : "red"
                                }
                                style={{ marginLeft: "8px" }}
                              >
                                {org.wallet.statut}
                              </Tag>
                            </p>
                          </Col>
                        </Row>
                      ) : (
                        <p>Aucune information de portefeuille disponible</p>
                      )}
                    </TabPane>

                    <TabPane
                      tab={
                        <span>
                          <QrcodeOutlined /> QR Code
                        </span>
                      }
                      key="3"
                    >
                      {org.qrCode ? (
                        <Row gutter={[16, 16]}>
                          <Col span={12}>
                            <p>
                              <strong>Code :</strong> {org.qrCode.code}
                            </p>
                            <p>
                              <strong>Statut :</strong>
                              <Tag
                                color={
                                  org.qrCode.status === "active"
                                    ? "green"
                                    : "red"
                                }
                                style={{ marginLeft: "8px" }}
                              >
                                {org.qrCode.status}
                              </Tag>
                            </p>
                          </Col>
                          <Col span={12}>
                            <p>
                              <strong>Créé le :</strong>{" "}
                              {new Date(
                                org.qrCode.createdAt
                              ).toLocaleDateString()}
                            </p>
                            <p>
                              <strong>MAJ le :</strong>{" "}
                              {new Date(
                                org.qrCode.updatedAt
                              ).toLocaleDateString()}
                            </p>
                          </Col>
                          <Col span={24}>
                            <p>
                              <strong>URL :</strong>
                            </p>
                            <a
                              href={org.qrCode.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {org.qrCode.url}
                            </a>
                          </Col>
                        </Row>
                      ) : (
                        <p>Aucune information de QR Code disponible</p>
                      )}
                    </TabPane>
                  </Tabs>
                </Card>
              ))}
            </div>
          )}
        </Modal>
      </div>
    </>
  );
}
